import type { TFunction } from 'i18next';
import { z } from 'zod';

// One definition of what a reset link must carry, shared by the route's validateSearch and the
// form, so the two can't drift: POST /auth/reset-password requires email and code, and Cognito
// confirmation codes are always 6 digits.
const resetPasswordSearchSchema = z.object({
  email: z.email(),
  code: z.union([
    z.string().length(6),
    z.number().refine(n => n.toString().length === 6, { message: 'Code must be 6 digits' }),
  ]),
});

const PASSWORD_MIN_LENGTH = 8;

// A factory, not a constant: this message reaches the user through <ErrorMessage />, which
// renders error.message verbatim, so it has to come from `t` like every other string on the page.
const makeResetPasswordFormSchema = (t: TFunction<'login'>) =>
  resetPasswordSearchSchema.extend({
    new_password: z.string().min(PASSWORD_MIN_LENGTH, t('form.errors.password.short', { min: PASSWORD_MIN_LENGTH })),
  });

// The backend answers with prose, not error codes (portal-backend's `_RESET_PASSWORD_ERRORS`), so
// these literals are the contract. resetLink.test.ts pins the mapping, not the contract — it feeds
// these same constants back in, so a backend reword still degrades every case to generic copy.
const BACKEND_INVALID_CODE = 'Invalid or expired password reset code';
const BACKEND_WEAK_PASSWORD = 'New password does not meet the password requirements';
const BACKEND_TOO_MANY_ATTEMPTS = 'Too many attempts. Please try again later.';

type ResetPasswordMessageKey =
  | 'form.errors.reset_password_failed'
  | 'form.errors.reset_password_link_invalid'
  | 'form.errors.reset_password_too_many'
  | 'form.errors.reset_password_weak';

type ResetPasswordFailure = {
  messageKey: ResetPasswordMessageKey;
  // False when the reset code survives the failure, so the user can retry in place instead of
  // being returned to /login with a link that was still usable.
  linkSpent: boolean;
};

function resetPasswordFailure(errorMessage: string | undefined): ResetPasswordFailure {
  if (errorMessage === BACKEND_INVALID_CODE) {
    return { messageKey: 'form.errors.reset_password_link_invalid', linkSpent: true };
  }

  if (errorMessage === BACKEND_WEAK_PASSWORD) {
    return { messageKey: 'form.errors.reset_password_weak', linkSpent: false };
  }

  // Cognito rate-limits before it validates the code, so the link survives this one too.
  if (errorMessage === BACKEND_TOO_MANY_ATTEMPTS) {
    return { messageKey: 'form.errors.reset_password_too_many', linkSpent: false };
  }

  // An unrecognised failure tells us nothing about the code, so keep the prior behaviour of
  // returning to /login rather than leaving the user on a form that may never succeed.
  return { messageKey: 'form.errors.reset_password_failed', linkSpent: true };
}

export {
  BACKEND_INVALID_CODE,
  BACKEND_TOO_MANY_ATTEMPTS,
  BACKEND_WEAK_PASSWORD,
  makeResetPasswordFormSchema,
  resetPasswordFailure,
  resetPasswordSearchSchema,
};
export type { ResetPasswordFailure, ResetPasswordMessageKey };
